import { useEffect } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import { queryKeys } from '../queryKeys';

// Each MF app owns its QueryClient, so a project write inside the Projects remote can't
// invalidate the shell's cached list; a window event is the only thing that crosses.
export const PROJECTS_CHANGED_EVENT = 'projects:changed';

/** Announce a project create/rename/archive/restore/delete. */
export function emitProjectsChanged(): void {
  if (typeof window === 'undefined') return;
  window.dispatchEvent(new CustomEvent(PROJECTS_CHANGED_EVENT));
}

// A bulk action or back-to-back writes must cost one list refetch, not one per signal.
const COALESCE_MS = 250;

/** Refetch this app's cached project lists whenever any app announces a project change. */
export function useRefreshProjectsOnChange(): void {
  const queryClient = useQueryClient();
  useEffect(() => {
    let timer: ReturnType<typeof setTimeout> | undefined;
    const onChanged = () => {
      if (timer) return;
      timer = setTimeout(() => {
        timer = undefined;
        void queryClient.invalidateQueries({ queryKey: [...queryKeys.projects.all, 'list'] });
      }, COALESCE_MS);
    };
    window.addEventListener(PROJECTS_CHANGED_EVENT, onChanged);
    return () => {
      window.removeEventListener(PROJECTS_CHANGED_EVENT, onChanged);
      if (timer) clearTimeout(timer);
    };
  }, [queryClient]);
}
